/**
 * Documents/Recipes Service
 * API calls for documents operations
 */

import { API_CONFIG } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';
import { getAuthToken } from '@/utils/auth';
import { getDownloadFileName } from '@/lib/downloadFileName';

export interface DocumentDTO {
  id: string;
  appointmentId: string;
  documentType: 'report' | 'prescription' | 'certificate' | 'recipe' | string;
  content: string;
  fileName?: string;
  fileUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  /** Provider interno unificado (T5/T9). LEGACY quando ausente. */
  provider?: 'LEGACY' | 'MEMED' | string;
  providerDocumentId?: string | null;
  digitalLink?: string | null;
  externalStatus?: string | null;
  renewedFromDocumentId?: string | null;
  renewedFromProviderId?: string | null;
  appointment?: {
    id: string;
    doctorId: string;
    patientId?: string;
    doctor?: {
      id: string;
      specialty?: string;
      user?: {
        name: string;
      };
      name?: string;
    };
    patient?: {
      id: string;
      name?: string;
      user?: {
        id?: string;
        name?: string;
        email?: string;
      };
    };
    date?: string;
    time?: string;
    reason?: string;
  };
}

export type DocumentAccessDTO = {
  id: string;
  type: string;
  provider: string;
  status: string;
  statusLabel: string;
  issuedAt?: string;
  canOpen: boolean;
  accessMode: 'INLINE_CONTENT' | 'DIGITAL_LINK';
  accessUrl: string | null;
  contentPreview: string | null;
  displayName: string;
};

export interface ConfirmMemedDocumentDTO {
  providerDocumentId: string;
  appointmentId: string;
  digitalLinkHint?: string;
  statusHint?: string;
  /** Documento local de origem (T10). Backend valida e deriva renewedFromProviderId. */
  renewedFromDocumentId?: string;
  /** Kind estável: prescription | recipe | certificate | exam. documentType é server-controlled. */
  kind?: 'prescription' | 'recipe' | 'certificate' | 'exam';
}

export type PrescriptionRenewalItem = {
  externalMedicationId?: string;
  name?: string;
  dosage?: string;
  concentration?: string;
  quantity?: string | number;
  route?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
};

export type PrescriptionRenewalBase = {
  sourceDocumentId: string;
  sourceProviderDocumentId?: string | null;
  sourceIssuedAt?: string | null;
  provider: 'MEMED' | string;
  externalStatus?: string | null;
  patient: {
    id: string;
    name?: string;
    birthDate?: string;
    gender?: string;
  };
  prescription?: {
    items?: PrescriptionRenewalItem[];
    notes?: string;
    issuedAt?: string | null;
  };
  prefillCapability: 'patient_only' | 'patient_and_items';
  reviewRequired: boolean;
  message?: string;
};

export interface CreateDocumentDTO {
  appointmentId: string;
  documentType: 'report' | 'prescription' | 'certificate' | 'recipe' | string;
  content: string;
  fileName?: string;
}

export interface UpdateDocumentDTO {
  content?: string;
  fileName?: string;
  fileUrl?: string;
}

/**
 * Get documents/recipes by appointment
 */
export async function getDocumentsByAppointment(
  appointmentId: string,
): Promise<ApiResponse<DocumentDTO[]>> {
  return apiClient.get(`/documents/appointment/${appointmentId}`);
}

/**
 * Get all documents/recipes
 */
export async function listDocuments(): Promise<ApiResponse<DocumentDTO[]>> {
  return apiClient.get('/documents');
}

/**
 * Get document by ID
 */
export async function getDocumentById(
  id: string,
): Promise<ApiResponse<DocumentDTO>> {
  return apiClient.get(`/documents/${id}`);
}

/**
 * Create document
 */
export async function createDocument(
  data: CreateDocumentDTO,
): Promise<ApiResponse<DocumentDTO>> {
  return apiClient.post('/documents', data);
}

/**
 * Confirma emissão Memed e persiste espelho local (T9).
 * Idempotente no backend — retry seguro com o mesmo providerDocumentId.
 * Aceita renewedFromDocumentId opcional (T10) — vínculo validado no backend.
 */
export async function confirmMemedDocument(
  data: ConfirmMemedDocumentDTO,
): Promise<ApiResponse<DocumentDTO> & { created?: boolean }> {
  const res = await apiClient.post<DocumentDTO>('/documents/memed', data);
  return res as ApiResponse<DocumentDTO> & { created?: boolean };
}

/**
 * Base normalizada para renovação (T10). Apenas médico autorizado.
 */
export async function getDocumentRenewalBase(
  documentId: string,
): Promise<ApiResponse<PrescriptionRenewalBase>> {
  return apiClient.get(`/documents/${documentId}/renewal-base`);
}

/**
 * Acesso normalizado LEGACY/MEMED (sem secrets do provider).
 */
export async function getDocumentAccess(
  documentId: string,
): Promise<ApiResponse<DocumentAccessDTO>> {
  return apiClient.get(`/documents/${documentId}/access`);
}

/**
 * Update document content
 */
export async function updateDocument(
  id: string,
  data: UpdateDocumentDTO,
): Promise<ApiResponse<DocumentDTO>> {
  return apiClient.put(`/documents/${id}`, data);
}

/**
 * Get all documents for a patient.
 * Aceita patients.id ou users.id — o backend resolve.
 * Preferir userId quando disponível.
 */
export async function getPatientDocuments(
  patientIdOrUserId: string,
  limit?: number,
): Promise<ApiResponse<DocumentDTO[]>> {
  const url = limit
    ? `/documents/patient/${patientIdOrUserId}?limit=${limit}`
    : `/documents/patient/${patientIdOrUserId}`;
  return apiClient.get(url);
}

/**
 * Get recipes (prescriptions) for a patient
 */
export async function getPatientRecipes(
  patientIdOrUserId: string,
): Promise<ApiResponse<DocumentDTO[]>> {
  return apiClient.get(`/documents/patient/${patientIdOrUserId}/recipes`);
}

export interface DocumentPdfDownload {
  blob: Blob;
  fileName: string;
}

/** Download PDF de documento clínico com nome retornado pelo backend. */
export async function downloadDocumentPdfFile(
  documentId: string,
): Promise<DocumentPdfDownload> {
  const res = await fetch(`${API_CONFIG.BASE_URL}/documents/${documentId}/pdf`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` },
  });
  if (!res.ok) {
    let message = 'Falha ao exportar PDF';
    try {
      const data = await res.json();
      message = data.error || data.message || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('application/pdf')) {
    throw new Error('O servidor não retornou um arquivo PDF válido');
  }

  const blob = await res.blob();
  if (blob.size === 0) {
    throw new Error('O PDF gerado está vazio');
  }

  return {
    blob,
    fileName: getDownloadFileName(
      res.headers.get('content-disposition'),
      `documento-${documentId}.pdf`,
    ),
  };
}

/** Compatibilidade com telas que apenas precisam do Blob. */
export async function downloadDocumentPdf(documentId: string): Promise<Blob> {
  return (await downloadDocumentPdfFile(documentId)).blob;
}

/** Envia documento por e-mail (paciente). */
export async function sendDocumentEmail(
  documentId: string,
  email?: string,
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  const res = await fetch(`${API_CONFIG.BASE_URL}/documents/${documentId}/send-email`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: email || '' }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    return {
      success: false,
      error: data.error || data.message || 'Erro ao enviar e-mail',
    };
  }
  return { success: true, data };
}

/** 
 * Fila de Renovação de Receitas 
 */

export interface RenewalRequestDTO {
  id: string;
  prescriptionId: string;
  patientId: string;
  status: 'REQUESTED' | 'IN_QUEUE' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  createdAt: string;
  prescription?: any;
  patient?: any;
}

export async function requestPrescriptionRenewal(
  prescriptionId: string,
): Promise<ApiResponse<RenewalRequestDTO>> {
  return apiClient.post(`/prescriptions/${prescriptionId}/renewal-requests`);
}

export async function getPatientRenewalRequests(): Promise<ApiResponse<RenewalRequestDTO[]>> {
  return apiClient.get('/patients/me/renewal-requests');
}

export async function getRenewalQueue(
  status?: string[],
): Promise<ApiResponse<RenewalRequestDTO[]>> {
  const query = status ? `?status=${status.join(',')}` : '';
  return apiClient.get(`/renewal-queue${query}`);
}

export async function claimRenewalRequest(
  requestId: string,
): Promise<ApiResponse<RenewalRequestDTO>> {
  return apiClient.post(`/renewal-queue/${requestId}/claim`);
}

export async function approveRenewalRequest(
  requestId: string,
  data: { medications?: any[]; observations?: string; expiresAt: string | Date },
): Promise<ApiResponse<any>> {
  return apiClient.post(`/renewal-queue/${requestId}/approve`, data);
}

export async function rejectRenewalRequest(
  requestId: string,
  reason: string,
): Promise<ApiResponse<RenewalRequestDTO>> {
  return apiClient.post(`/renewal-queue/${requestId}/reject`, { reason });
}

